import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AdminReferrals() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: response } = await api.get("/admin/referrals");
      setData(response);
      setSettings(response.settings);
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      const { data: next } = await api.put("/admin/referrals/settings", settings);
      setSettings(next);
      toast.success("Referral settings saved.");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save referral settings.");
    }
  };

  const cancelReferral = async (id) => {
    await api.post(`/admin/referrals/${id}/cancel`, { reason: "Admin review" });
    toast.success("Referral cancelled.");
    load();
  };

  const revokeReward = async (id) => {
    await api.post(`/admin/referral-rewards/${id}/revoke`, { reason: "Admin review" });
    toast.success("Reward revoked.");
    load();
  };

  if (!data || !settings) return <main className="p-8 text-espresso-light">Loading referrals…</main>;

  const fields = [
    ["discount_percent", "Friend discount %"],
    ["minimum_order_value", "Minimum food subtotal"],
    ["referrals_per_reward", "Friends per free cup"],
    ["reward_expiry_days", "Reward expiry days"],
  ];

  return (
    <main data-testid="admin-referrals-page" className="min-h-screen bg-ivory px-6 py-10 md:px-10">
      <div className="mx-auto max-w-7xl">
        <Link to="/admin/dashboard" data-testid="admin-referrals-back" className="label-xs text-espresso-light underline">
          ← Admin dashboard
        </Link>
        <h1 className="mt-4 font-serif text-5xl text-espresso">Referrals</h1>
        <section className="mt-8 grid gap-4 border-y border-line py-6 md:grid-cols-4">
          {fields.map(([key, label]) => (
            <label key={key} className="text-xs text-espresso-light">
              {label}
              <input
                type="number"
                value={settings[key]}
                onChange={(event) => setSettings({ ...settings, [key]: Number(event.target.value) })}
                data-testid={`referral-setting-${key}`}
                className="mt-2 w-full border-b border-espresso bg-transparent py-2 text-lg text-espresso outline-none"
              />
            </label>
          ))}
        </section>
        <button onClick={save} data-testid="save-referral-settings" className="btn-espresso mt-5">Save settings</button>

        <section className="mt-12">
          <div className="label-xs text-espresso-light">Codes and rewards</div>
          <div className="mt-4 overflow-x-auto border border-line">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-[0.12em] text-espresso-light">
                <tr><th className="p-4">Owner</th><th className="p-4">Code</th><th className="p-4">Counted</th><th className="p-4">Earned / used</th></tr>
              </thead>
              <tbody>
                {data.codes.map((row) => (
                  <tr key={row.code} className="border-b border-line last:border-0">
                    <td className="p-4">{row.owner_name}<div className="text-xs text-espresso-light">{row.owner_email}</div></td>
                    <td className="p-4 font-mono">{row.code}</td>
                    <td className="p-4">{row.successful_referrals}</td>
                    <td className="p-4">{row.rewards_earned} / {row.rewards_used}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div><div className="label-xs text-espresso-light">Referral review</div>{data.referrals.map((item) => (
            <div key={item.id} className="mt-3 flex items-center justify-between border-b border-line py-3 text-sm">
              <span>{item.code} · {item.friend_first_name} · {item.status}</span>
              {item.status !== "cancelled" && <button onClick={() => cancelReferral(item.id)} data-testid={`cancel-referral-${item.id}`} className="text-xs text-red-700 underline">Cancel</button>}
            </div>
          ))}</div>
          <div><div className="label-xs text-espresso-light">Reward review</div>{data.rewards.map((item) => (
            <div key={item.id} className="mt-3 flex items-center justify-between border-b border-line py-3 text-sm">
              <span>{item.status} · {new Date(item.expires_at).toLocaleDateString("en-IN")}</span>
              {["available", "reserved"].includes(item.status) && <button onClick={() => revokeReward(item.id)} data-testid={`revoke-reward-${item.id}`} className="text-xs text-red-700 underline">Revoke</button>}
            </div>
          ))}</div>
        </section>
      </div>
    </main>
  );
}